import axios from 'axios'
import { API_BASE_URL } from './client'

// ── โดเมนหลักสำหรับสร้างลิงก์ของแต่ละ tenant (เช่น "acme" -> https://acme.assetsservice.com)
// ต้องตั้งผ่าน VITE_TENANT_BASE_DOMAIN เท่านั้น ไม่มี fallback เป็นโดเมนจริงของ deployment ไหน
// เลย — เหตุผลเดียวกับ VITE_API_URL ใน client.ts: ถ้าลืมตั้งแล้วมี default silent ไปเดาโดเมนผิด
// จะสร้างลิงก์ที่หน้าตาเหมือนใช้งานได้แต่พาไปคนละที่แบบไม่มี error ให้เห็นเลย ──
function getTenantBaseDomain(): string | null {
  const configured = import.meta.env.VITE_TENANT_BASE_DOMAIN
  if (configured) return configured
  console.warn(
    '[Config] VITE_TENANT_BASE_DOMAIN ไม่ได้ถูกตั้งค่า — ลิงก์ของ tenant จะสร้างไม่ได้ ' +
    'ตรวจสอบ Environment Variable ของ frontend service',
  )
  return null
}

export const TENANT_BASE_DOMAIN = getTenantBaseDomain()

/** คืน URL เต็มของ tenant นั้น หรือ null ถ้ายังไม่ได้ตั้งค่า TENANT_BASE_DOMAIN ไว้ */
export function buildTenantUrl(subdomain: string): string | null {
  if (!TENANT_BASE_DOMAIN) return null
  return `https://${subdomain}.${TENANT_BASE_DOMAIN}`
}

// ── แยก axios instance ต่างหากจาก client.ts เดิม — เพราะ Platform Admin ใช้ token คนละก้อน
// (platform_token) และ redirect คนละหน้าตอน 401 (/platform/login ไม่ใช่ /login ของ tenant) —
// ถ้าใช้ instance เดียวกันจะสับสนว่า request ไหนควรแนบ token ไหน โดยเฉพาะช่วงที่คนใช้เปิด
// 2 tab พร้อมกัน (tab หนึ่ง login เป็น tenant admin ปกติ อีก tab login เป็น platform admin) ──
const platformApi = axios.create({ baseURL: API_BASE_URL })

platformApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('platform_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

let isRedirecting = false
platformApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && !isRedirecting) {
      // ── ยกเว้นทั้ง login และ change-password จาก auto-redirect — ทั้งคู่ควรโชว์ error message
      // ของตัวเองบนหน้าเดิม ไม่ใช่โดนเตะกลับไปหน้า login แบบเงียบๆ ก่อนจะทันเห็น error จริง
      // (เจอปัญหานี้ตรงๆ ตอนทดสอบจริง — เปลี่ยนรหัสผ่านไม่ผ่านแล้วโดน redirect ทันที มองไม่เห็น
      // สาเหตุที่แท้จริงเลยว่าทำไม 401) ──
      const url = err.config?.url ?? ''
      const isExcluded = url.includes('/platform/auth/login') || url.includes('/platform/auth/change-password')
      if (!isExcluded) {
        isRedirecting = true
        localStorage.removeItem('platform_token')
        window.location.href = '/platform/login'
      }
    }
    return Promise.reject(err)
  },
)

export default platformApi

export const platformLogin = (username: string, password: string) =>
  platformApi.post('/platform/auth/login', { username, password })

export const platformChangePassword = (currentPassword: string, newPassword: string) =>
  platformApi.post('/platform/auth/change-password', { currentPassword, newPassword })

export const listTenants = () => platformApi.get('/platform/tenants')

export const createTenant = (data: { subdomain: string; displayName: string; adminEmail: string; plan?: string }) =>
  platformApi.post('/platform/tenants', data)

export const getTenantStats = (id: number) => platformApi.get(`/platform/tenants/${id}/stats`)

export const setTenantActive = (id: number, isActive: boolean) =>
  platformApi.patch(`/platform/tenants/${id}/active`, { isActive })

export const resetAdminPassword = (id: number) =>
  platformApi.post(`/platform/tenants/${id}/reset-admin-password`)

export const updateTenantName = (id: number, displayName: string) =>
  platformApi.patch(`/platform/tenants/${id}/name`, { displayName })

export const updateTenantContractDetails = (id: number, dto: {
  plan?: string
  goLiveDate?: string | null
  contractStartDate?: string | null
  contractExpiryDate?: string | null
  notificationEmails?: string | null
}) => platformApi.patch(`/platform/tenants/${id}/contract`, dto)

export const updateTenantIntegrationSettings = (id: number, dto: {
  cloudinaryCloudName?: string | null
  cloudinaryApiKey?: string | null
  cloudinaryApiSecret?: string | null
  lineChannelSecret?: string | null
  lineChannelAccessToken?: string | null
  emailFromName?: string | null
  emailFromAddress?: string | null
}) => platformApi.patch(`/platform/tenants/${id}/integrations`, dto)

export const deleteTenant = (id: number) =>
  platformApi.delete(`/platform/tenants/${id}`)

export const updateTenantPlan = (id: number, plan: string) =>
  platformApi.patch(`/platform/tenants/${id}/plan`, { plan })

// ── ตั้งค่าอีเมลระดับ "ทั้งระบบ" (ไม่ผูก tenant ไหน) — ใช้ส่งอีเมลของแพลตฟอร์มเอง เช่น
// คำขอ Demo จากหน้าเว็บขาย (www.assetsservice.com) ──
export type PlatformEmailProvider = 'smtp' | 'resend'

export interface PlatformEmailSettings {
  provider: PlatformEmailProvider
  smtpHost: string | null
  smtpPort: number | null
  smtpSecure: boolean
  smtpUser: string | null
  hasPassword: boolean
  hasResendApiKey: boolean
  fromName: string | null
  fromAddress: string | null
  notifyEmails: string | null
  updatedAt: string
}

export const getPlatformEmailSettings = () =>
  platformApi.get<PlatformEmailSettings>('/platform/settings/email')

export const updatePlatformEmailSettings = (dto: {
  provider?: PlatformEmailProvider | null
  smtpHost?: string | null
  smtpPort?: number | null
  smtpSecure?: boolean | null
  smtpUser?: string | null
  // undefined = ไม่แตะรหัสผ่านเดิม, '' = ลบรหัสผ่านทิ้ง, ค่าอื่น = ตั้งรหัสผ่านใหม่
  smtpPassword?: string
  // เหมือนกัน: undefined = ไม่แตะ, '' = ลบ, ค่าอื่น = ตั้งใหม่
  resendApiKey?: string
  fromName?: string | null
  fromAddress?: string | null
  notifyEmails?: string | null
}) => platformApi.put<PlatformEmailSettings>('/platform/settings/email', dto)

export const testPlatformEmail = (toEmail: string) =>
  platformApi.post('/platform/settings/email/test', { toEmail })

// ── สถิติเว็บขาย — นับอย่างเดียว ไม่มี IP/identity ──
export interface WebsiteAnalyticsSummary {
  totalPageviews: number
  totalClicks: number
  pageviews7d: number
  pageviews30d: number
  clicks7d: number
  clicks30d: number
  daily: { date: string; pageviews: number; clicks: number }[]
  topClicks: { label: string; count: number }[]
}

export const getWebsiteAnalyticsSummary = () =>
  platformApi.get<WebsiteAnalyticsSummary>('/platform/analytics/summary')

// ── ราคาแพ็กเกจที่โชว์บนหน้าเว็บขาย — แก้จาก Settings > Plan แล้วหน้าเว็บอัปเดตทันที
// ไม่มีข้อมูลลับ (แค่ราคา/ข้อความโฆษณา) ──
export interface PlatformPricing {
  monthlyPrice: number
  annualOriginalPrice: number
  annualPrice: number
  currency: string
  features: string[]
  updatedAt: string
}

export const getPlatformPricing = () =>
  platformApi.get<PlatformPricing>('/platform/settings/pricing')

export const updatePlatformPricing = (dto: {
  monthlyPrice?: number
  annualOriginalPrice?: number
  annualPrice?: number
  currency?: string
  features?: string[]
}) => platformApi.put<PlatformPricing>('/platform/settings/pricing', dto)
